/**
 * Auto-reconnecting WebSocket client.
 *
 * The Nigastock WebSocket API requires the JWT in the `Authorization` query
 * parameter (browsers cannot set headers on WS handshakes). On every event we
 * dispatch to subscribers keyed by `type` (FillEmitted, PositionUpdated, …).
 *
 * Reconnect strategy: exponential backoff with jitter, capped at 30 seconds.
 * Tokens are pulled fresh on each connect via `getIdToken` so a near-expiry
 * token never strands the client.
 */
export type WsEventType =
  | 'FillEmitted' | 'PositionUpdated' | 'L2Tick' | 'OrderStatusChanged';

export interface WsEvent {
  type: WsEventType;
  payload: unknown;
}

export type WsListener = (e: WsEvent) => void;
export type WsStatus = 'connecting' | 'open' | 'closed' | 'error';
export type WsStatusListener = (s: WsStatus) => void;

export interface WsClientConfig {
  endpoint: string;        // wss://abc123.execute-api.eu-west-1.amazonaws.com/v1
  getIdToken: () => Promise<string | undefined>;
  /** Override for tests. */
  WebSocketImpl?: typeof WebSocket;
}

export class WsClient {
  private ws?: WebSocket;
  private listeners = new Set<WsListener>();
  private statusListeners = new Set<WsStatusListener>();
  private retryAttempt = 0;
  private closed = false;

  constructor(private readonly cfg: WsClientConfig) {}

  on(fn: WsListener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }
  onStatus(fn: WsStatusListener): () => void {
    this.statusListeners.add(fn);
    return () => this.statusListeners.delete(fn);
  }

  async connect(): Promise<void> {
    if (this.closed) return;
    this.emitStatus('connecting');
    const token = await this.cfg.getIdToken();
    if (!token) { this.emitStatus('error'); this.scheduleReconnect(); return; }

    const Impl = this.cfg.WebSocketImpl ?? WebSocket;
    const url = `${this.cfg.endpoint}?Authorization=${encodeURIComponent(token)}`;
    const ws = new Impl(url);
    this.ws = ws;

    ws.addEventListener('open', () => {
      this.retryAttempt = 0;
      this.emitStatus('open');
    });
    ws.addEventListener('message', (msg: MessageEvent) => {
      try {
        const data = JSON.parse(typeof msg.data === 'string' ? msg.data : String(msg.data));
        if (data && typeof data.type === 'string') {
          this.listeners.forEach((fn) => fn(data as WsEvent));
        }
      } catch {
        // ignore non-JSON frames
      }
    });
    ws.addEventListener('close', () => {
      this.emitStatus('closed');
      if (!this.closed) this.scheduleReconnect();
    });
    ws.addEventListener('error', () => {
      this.emitStatus('error');
    });
  }

  close(): void {
    this.closed = true;
    this.ws?.close();
  }

  private scheduleReconnect(): void {
    const base = Math.min(30_000, 500 * Math.pow(2, this.retryAttempt));
    const jitter = Math.random() * 250;
    this.retryAttempt += 1;
    setTimeout(() => { void this.connect(); }, base + jitter);
  }

  private emitStatus(s: WsStatus): void {
    this.statusListeners.forEach((fn) => fn(s));
  }
}
